import { uiSurface } from "shared/ui/uiSurface"
import { Business, Clear, Email, Person } from "@mui/icons-material"
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ClientSummary } from "../../../apis/appApi"
import { useClientSearch } from "./useClientSearch"
import type { OrderCreateFormState } from "./useOrderCreateForm"

type Props = {
  lastName: string
  firstName: string
  selectedClient: OrderCreateFormState["selectedClient"]
  onLastNameChange: (value: string) => void
  onFirstNameChange: (value: string) => void
  onSelectClient: (client: ClientSummary) => void
  onClearClient: () => void
}

export const CustomerNameAutocomplete: FC<Props> = ({
  lastName,
  firstName,
  selectedClient,
  onLastNameChange,
  onFirstNameChange,
  onSelectClient,
  onClearClient,
}) => {
  const { t } = useTranslation()
  const { results, isLoading, search, clear } = useClientSearch()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const query = `${lastName} ${firstName}`.trim()

  useEffect(() => {
    if (!selectedClient && query.length >= 3) {
      search(query)
      setOpen(true)
    } else {
      clear()
      setOpen(false)
    }
  }, [query, selectedClient, search, clear])

  const handleSelect = useCallback(
    (client: ClientSummary) => {
      onSelectClient(client)
      setOpen(false)
      clear()
    },
    [onSelectClient, clear],
  )

  const handleClear = useCallback(() => {
    onClearClient()
    clear()
  }, [onClearClient, clear])

  return (
    <Box {...uiSurface("UI-DLV2-036")} ref={anchorRef}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          data-testid="order-create-lastName"
          label={t("orderCreate.lastName")}
          value={lastName}
          onChange={(e) => onLastNameChange(e.target.value)}
          size="small"
          fullWidth
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Person fontSize="small" color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          data-testid="order-create-firstName"
          label={t("orderCreate.firstName")}
          value={firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          size="small"
          fullWidth
          slotProps={{
            input: {
              endAdornment: selectedClient ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClear}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : isLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Stack>

      {selectedClient && (
        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: "block" }}>
          {t("orderCreate.existingClient")}: #{selectedClient.id}
        </Typography>
      )}

      <Popper
        open={open && results.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1400, width: anchorRef.current?.offsetWidth }}
      >
        <Paper elevation={8} sx={{ maxHeight: 300, overflow: "auto", borderRadius: 2 }}>
          <List disablePadding>
            {results.map((client, idx) => {
              const initials = `${(client.firstName?.[0] || "").toUpperCase()}${(client.lastName?.[0] || "").toUpperCase()}`
              return (
                <Box key={client.id}>
                  {idx > 0 && <Divider />}
                  <ListItemButton
                    data-testid={`order-create-client-option-${client.id}`}
                    onClick={() => handleSelect(client)}
                    sx={{ py: 1.25, px: 2 }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: 14,
                          fontWeight: 600,
                          bgcolor: "primary.main",
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {client.lastName}, {client.firstName}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                          {client.organisation && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Business sx={{ fontSize: 13, color: "text.disabled" }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {client.organisation}
                              </Typography>
                            </Stack>
                          )}
                          {client.email && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Email sx={{ fontSize: 13, color: "text.disabled" }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {client.email}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.disabled",
                          fontFamily: "monospace",
                          fontSize: 11,
                          flexShrink: 0,
                        }}
                      >
                        #{client.id}
                      </Typography>
                    </Stack>
                  </ListItemButton>
                </Box>
              )
            })}
          </List>
        </Paper>
      </Popper>
    </Box>
  )
}
